if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Add validation for dynamic payment buttons (Buy it now)
        // Try immediately
        this.setupDynamicPaymentButtonValidation();

        // Also setup observer to catch buttons loaded after page load
        this.observePaymentButtons();
      }

      observePaymentButtons() {
        // Watch for dynamic payment buttons being added (they load async)
        const observer = new MutationObserver(mutations => {
          const hasNewButtons = mutations.some(mutation =>
            Array.from(mutation.addedNodes).some(
              node =>
                node.nodeType === 1 &&
                (node.classList?.contains('shopify-payment-button') ||
                  node.querySelector?.('.shopify-payment-button'))
            )
          );

          if (hasNewButtons) {
            this.setupDynamicPaymentButtonValidation();
          }
        });

        // Observe this product form for changes
        observer.observe(this, {
          childList: true,
          subtree: true,
        });
      }

      setupDynamicPaymentButtonValidation() {
        // Shopify's dynamic payment buttons bypass normal form submission
        // We need to intercept and validate before they process
        const paymentButtons = this.querySelectorAll(
          '.shopify-payment-button__button'
        );

        if (paymentButtons.length > 0) {
          // Initialize tracked buttons set if not exists
          if (!this.validatedButtons) {
            this.validatedButtons = new WeakSet();
          }

          paymentButtons.forEach(button => {
            // Skip if already has validation attached
            if (this.validatedButtons.has(button)) {
              return;
            }

            // Mark this button as validated
            this.validatedButtons.add(button);

            // Store handler reference so we can remove/re-add it
            const validationHandler = async evt => {
              // Always prevent default first to stop immediate redirect
              evt.preventDefault();
              evt.stopPropagation();
              evt.stopImmediatePropagation();

              // Validate quantity - skip cart check for Buy it now button
              const isValid = await this.validateQuantityForRentalPurchase(
                true
              );
              if (!isValid) {
                // Validation failed, do nothing (already showed toast)
                return false;
              }

              // Validation passed - trigger the actual payment button action
              // Remove our listener temporarily and click again
              button.removeEventListener('click', validationHandler, true);
              button.click();

              // Re-attach listener for future clicks
              setTimeout(() => {
                button.addEventListener('click', validationHandler, true);
              }, 100);
            };

            button.addEventListener('click', validationHandler, true);
          });
        }
      }

      async validateQuantityForRentalPurchase(skipCartCheck = false) {
        const formData = new FormData(this.form);
        const quantity = parseInt(formData.get('quantity')) || 1;
        const variantId = formData.get('id');
        let variantTitle = '';

        // Try to get variant title from ProductVariants JSON (most reliable)
        const productVariantsScript = document.querySelector(
          'script[id^="ProductVariants-"]'
        );
        if (productVariantsScript) {
          try {
            const productData = JSON.parse(productVariantsScript.textContent);
            const variant = productData.variants.find(
              v => v.id.toString() === variantId
            );
            if (variant) {
              variantTitle = variant.title;
            }
          } catch (e) {
            console.warn('Could not parse ProductVariants JSON:', e);
          }
        }

        // Fallback: try to get from selected option or radio button
        if (!variantTitle) {
          const variantSelector = this.form.querySelector(
            `input[name="id"][value="${variantId}"]`
          );
          if (variantSelector && variantSelector.dataset.variantTitle) {
            variantTitle = variantSelector.dataset.variantTitle;
          } else {
            const selectedOption = this.form.querySelector(
              'select[name="id"] option:checked'
            );
            if (selectedOption) {
              variantTitle =
                selectedOption.textContent ||
                selectedOption.getAttribute('data-title') ||
                '';
            }
          }
        }

        // Determine if this is a Rental or Purchase product
        // Check for multiple languages: English, Korean (ko), Chinese (zh-CN)
        const rentalKeywords = [
          'Rental', // English
          '임대', // Korean
          '租赁',
          '租賃', // Chinese Simplified & Traditional
          '出租', // Chinese - rental
          '参观服务',
          '參觀服務', // Chinese - visit service (used in rental context)
        ];

        const purchaseKeywords = [
          'Purchase', // English
          '구입',
          '구매', // Korean
          '购买',
          '購買', // Chinese Simplified & Traditional
          '买', // Chinese - buy
        ];

        // Debug logging to see actual variant title
        console.log('Variant title for validation:', variantTitle);

        const isRental = rentalKeywords.some(keyword =>
          variantTitle.includes(keyword)
        );
        const isPurchase = purchaseKeywords.some(keyword =>
          variantTitle.includes(keyword)
        );
        const isRentalOrPurchase = isRental || isPurchase;

        // Debug logging
        console.log(
          'Is Rental:',
          isRental,
          'Is Purchase:',
          isPurchase,
          'Should validate:',
          isRentalOrPurchase,
          'Skip cart check:',
          skipCartCheck
        );

        // Check quantity limit for Rental/Purchase products
        if (isRentalOrPurchase) {
          const quantityInput = this.form.querySelector('[name="quantity"]');

          // Check if adding quantity exceeds limit per product
          if (quantity > 4) {
            // Reset quantity to maximum allowed
            if (quantityInput) {
              quantityInput.value = 4;
            }

            // Show toast message
            if (typeof toastr !== 'undefined') {
              toastr.warning(
                'Maximum quantity for Rental and Purchase products is 4',
                'Quantity Limit',
                {
                  timeOut: 3000,
                  positionClass: 'toast-top-right',
                }
              );
            } else {
              alert('Maximum quantity for Rental and Purchase products is 4');
            }

            return false;
          }

          // Skip cart validation if requested (for "Buy it now" button)
          if (skipCartCheck) {
            return true;
          }

          // Fetch current cart data from Shopify API to get accurate quantity
          let cartQuantity = 0; // Quantity of this specific variant in cart
          let totalRentalPurchaseQty = 0; // Total of ALL Rental/Purchase products in cart

          try {
            const response = await fetch('/cart.js');
            const cart = await response.json();

            // Count total Rental/Purchase products in cart
            cart.items.forEach(item => {
              const itemTitle = item.title || '';

              // Check if this cart item is Rental or Purchase
              const itemIsRental = rentalKeywords.some(keyword =>
                itemTitle.includes(keyword)
              );
              const itemIsPurchase = purchaseKeywords.some(keyword =>
                itemTitle.includes(keyword)
              );

              if (itemIsRental || itemIsPurchase) {
                totalRentalPurchaseQty += item.quantity;
              }

              // Also track quantity of this specific variant
              if (item.variant_id.toString() === variantId) {
                cartQuantity = item.quantity;
              }
            });
          } catch (e) {
            console.warn('Could not fetch cart data:', e);
            // Fallback to data attribute if API fails
            if (quantityInput && quantityInput.dataset.cartQuantity) {
              cartQuantity = parseInt(quantityInput.dataset.cartQuantity) || 0;
              totalRentalPurchaseQty = cartQuantity; // Best guess
            }
          }

          // Calculate totals
          const totalQuantity = cartQuantity + quantity; // Total for this variant
          const newTotalRentalPurchase = totalRentalPurchaseQty + quantity; // New total across all Rental/Purchase

          // NEW: Check if total Rental/Purchase products in cart would exceed 4
          if (newTotalRentalPurchase > 4) {
            const allowedToAdd = 4 - totalRentalPurchaseQty;

            // Reset quantity to maximum allowed
            if (quantityInput && allowedToAdd > 0) {
              quantityInput.value = allowedToAdd;
            }

            // Show appropriate toast message
            if (typeof toastr !== 'undefined') {
              if (totalRentalPurchaseQty >= 4) {
                toastr.warning(
                  `Your cart already has ${totalRentalPurchaseQty} Rental/Purchase products. Maximum is 4 total.`,
                  'Cart Limit Reached',
                  {
                    timeOut: 4000,
                    positionClass: 'toast-top-right',
                  }
                );
              } else {
                toastr.warning(
                  `You have ${totalRentalPurchaseQty} Rental/Purchase products in cart. You can only add ${allowedToAdd} more (max 4 total).`,
                  'Quantity Limit',
                  {
                    timeOut: 4000,
                    positionClass: 'toast-top-right',
                  }
                );
              }
            } else {
              if (totalRentalPurchaseQty >= 4) {
                alert(
                  `Your cart already has ${totalRentalPurchaseQty} Rental/Purchase products. Maximum is 4 total.`
                );
              } else {
                alert(
                  `You have ${totalRentalPurchaseQty} Rental/Purchase products in cart. You can only add ${allowedToAdd} more (max 4 total).`
                );
              }
            }

            return false;
          }

          // Check if total quantity for this specific variant exceeds limit
          if (totalQuantity > 4) {
            const allowedToAdd = 4 - cartQuantity;

            // Reset quantity to maximum allowed
            if (quantityInput && allowedToAdd > 0) {
              quantityInput.value = allowedToAdd;
            }

            // Show appropriate toast message
            if (typeof toastr !== 'undefined') {
              if (cartQuantity >= 4) {
                toastr.warning(
                  `You already have ${cartQuantity} of this item in cart. Maximum is 4 per product.`,
                  'Cart Limit Reached',
                  {
                    timeOut: 4000,
                    positionClass: 'toast-top-right',
                  }
                );
              } else {
                toastr.warning(
                  `You have ${cartQuantity} in cart. You can only add ${allowedToAdd} more of this product (max 4).`,
                  'Quantity Limit',
                  {
                    timeOut: 4000,
                    positionClass: 'toast-top-right',
                  }
                );
              }
            } else {
              if (cartQuantity >= 4) {
                alert(
                  `You already have ${cartQuantity} of this item in cart. Maximum is 4 per product.`
                );
              } else {
                alert(
                  `You have ${cartQuantity} in cart. You can only add ${allowedToAdd} more of this product (max 4).`
                );
              }
            }

            return false;
          }
        }

        return true; // Validation passed
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Validate quantity for Rental and Purchase products
        const isValid = await this.validateQuantityForRentalPurchase();
        if (!isValid) {
          // Validation failed - re-enable submit button and return
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map(section => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              'add:wait-for-subscribers'
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
